import Toggle from '@cloudscape-design/components/toggle'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Alert from '@cloudscape-design/components/alert'
import Select from '@cloudscape-design/components/select'
import Link from '@cloudscape-design/components/link'
import { useAppStore } from '@/store/app-store'
import { filterPresetsByVendors, applyBedrockRegion, getVendorFromPreset } from '@/utils/vendor-filter'
import type { PresetProfile, Vendor, BedrockRegionKey } from '@/types'
import { PresetProfileCard } from '@/components/profiles/PresetProfileCard'
import gridStyles from './VendorFilterPanel.module.css'

interface VendorFilterPanelProps {
  presets: PresetProfile[]
  onPresetUpdate: (updated: PresetProfile) => void
  onPresetToggle: (id: string) => void
}

const VENDOR_TOGGLES: { vendor: Vendor; label: string }[] = [
  { vendor: 'bedrock', label: 'Amazon Bedrock' },
  { vendor: 'anthropic-direct', label: 'Anthropic' },
  { vendor: 'grok', label: 'Grok (xAI)' },
]

const REGION_OPTIONS: { value: BedrockRegionKey; label: string }[] = [
  { value: 'US', label: 'US' },
  { value: 'EU', label: 'EU' },
  { value: 'AP', label: 'AU' },
  { value: 'Global', label: 'Global' },
]

export function VendorFilterPanel({ presets, onPresetUpdate, onPresetToggle }: VendorFilterPanelProps) {
  const selectedVendors = useAppStore((s) => s.selectedVendors)
  const toggleVendor = useAppStore((s) => s.toggleVendor)
  const bedrockRegion = useAppStore((s) => s.bedrockRegion)
  const setBedrockRegion = useAppStore((s) => s.setBedrockRegion)
  const hasAnthropicKey = useAppStore((s) => s.hasAnthropicKey)
  const hasXaiKey = useAppStore((s) => s.hasXaiKey)
  const executionMode = useAppStore((s) => s.executionMode)
  const credentialStatus = useAppStore((s) => s.credentialStatus)

  const filtered = filterPresetsByVendors(presets, selectedVendors)

  // Apply Bedrock region rewriting to Bedrock presets
  const displayPresets = filtered.map((p) =>
    getVendorFromPreset(p) === 'bedrock' ? applyBedrockRegion(p, bedrockRegion) : p
  )

  return (
    <SpaceBetween size="m">
      <SpaceBetween direction="horizontal" size="l">
        {VENDOR_TOGGLES.map(({ vendor, label }) => (
          <Toggle
            key={vendor}
            checked={selectedVendors.has(vendor)}
            onChange={() => toggleVendor(vendor)}
          >
            {label}
          </Toggle>
        ))}
        {selectedVendors.has('bedrock') && (
          <Select
            selectedOption={{ value: bedrockRegion, label: bedrockRegion }}
            onChange={({ detail }) =>
              setBedrockRegion(detail.selectedOption.value as BedrockRegionKey)
            }
            options={REGION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
        )}
      </SpaceBetween>

      {executionMode === 'local' && selectedVendors.has('anthropic-direct') && !hasAnthropicKey && (
        <Alert type="info">
          Anthropic API key not configured. Add your ANTHROPIC_API_KEY in the <Link onFollow={(e) => { e.preventDefault(); useAppStore.getState().setPage('settings') }}>Settings</Link> page.
        </Alert>
      )}

      {executionMode === 'local' && selectedVendors.has('grok') && !hasXaiKey && (
        <Alert type="info">
          Grok (xAI) API key not configured. Add your XAI_API_KEY in the <Link onFollow={(e) => { e.preventDefault(); useAppStore.getState().setPage('settings') }}>Settings</Link> page.
        </Alert>
      )}

      {executionMode === 'local' && selectedVendors.has('bedrock') && (
        credentialStatus === 'verified' ? (
          <Alert type="success">Bedrock Invoke Ready</Alert>
        ) : (
          <Alert type="warning">
            AWS credentials required for Bedrock profiles. Go to <Link onFollow={(e) => { e.preventDefault(); useAppStore.getState().setPage('settings') }}>Settings</Link> to configure.
          </Alert>
        )
      )}

      {selectedVendors.size === 0 ? (
        <Box textAlign="center" color="text-body-secondary" padding="l">
          No vendors selected. Toggle a vendor above to see preset profiles.
        </Box>
      ) : (
        <div className={gridStyles.grid}>
          {displayPresets.map((preset) => (
            <PresetProfileCard
              key={preset.id}
              preset={preset}
              onUpdate={onPresetUpdate}
              onToggle={onPresetToggle}
            />
          ))}
        </div>
      )}
    </SpaceBetween>
  )
}
